import React, { useLayoutEffect, useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import PullToRefresh from "react-simple-pull-to-refresh";

// layouts
import { MainLayout } from "layouts";

// components
import {
  PopdownFilter,
  Tabs,
  FilterCard,
  BottomBranchSelect,
} from "components/common";
import Observer from "components/ui/Observer";
import Text from "components/ui/Text";
import PullToRefreshing from "components/common/PullToRefreshing";
import AppLinkRequestCard from "components/all/app-link/AppLinkRequestCard";
import AppLinkRequestPopup from "components/all/app-link/AppLinkRequestPopup";

// hooks
import useDidMountEffect from "hooks/useDidMountEffect";

// assets
import SearchIcon from "assets/icons/search-icon.svg";

// utils
import { getCalendar } from "utils/date";

// redux
import { fetchRequests, resetLinkRequests } from "redux/slices/link/linkSlice";

const TAB_OPTIONS = [
  { tabKey: 0, value: "ALL", label: "전체" },
  { tabKey: 1, value: "DONE", label: "완료" },
  { tabKey: 2, value: "CANCEL", label: "거절" },
];

// default dates
const newDate = new Date();
const startDt = getCalendar(newDate.getStartDateInMonth(), "yyyy-mm-dd");
const endDt = getCalendar(new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0), "yyyy-mm-dd");

const AppLinkRequests = () => {
  const dispatch = useDispatch();
  const { seqPartnerBranch, branchUseYn } = useSelector((state) => state.partner);
  const { list, pagination, counts, loading } = useSelector((state) => state.link);
  const showBranchSelect = branchUseYn === "Y" && seqPartnerBranch === 0;

  const initialFilterValues = {
    startDt: startDt,
    endDt: endDt,
    period: "CURRENT_MONTH",
    seqPartnerBranch: seqPartnerBranch,
    searchTerm: "",
  };
  const [activeTab, setActiveTab] = useState(0);
  const [filter, setFilter] = useState(initialFilterValues);
  const [filterVisible, setFilterVisible] = useState(false);
  const [userCard, setUserCard] = useState({ open: false, requestInfo: {} });
  const [searchCount, setSearchCount] = useState(0);

  const resetFilter = () => {
    setSearchCount(0);
    setFilter(prevState => ({...initialFilterValues, seqPartnerBranch: prevState.seqPartnerBranch} ));
  };

  const handleFilterBranchChange = (branch) => {
    setFilter((prevState) => ({
      ...prevState,
      seqPartnerBranch: branch,
    }));
  };

  const handleClickTab = (clickedTab) => {
    if (activeTab === clickedTab) return;
    setActiveTab(clickedTab);
  };

  const getTabCounts = () => {
    return branchUseYn === "Y" && filter.seqPartnerBranch === 0
      ? {
          ALL: 0,
          DONE: 0,
          CANCEL: 0,
        }
      : {
          ALL: counts?.totalMemberCount,
          DONE: counts?.approvedMemberCount,
          CANCEL: counts?.canceledMemberCount,
        };
  };

  const getInfoMessage = () => {
    if (searchCount > 0) {
      if (pagination.pageState === false && !loading && !list.length)
        return (
          <Message>
            <Text lineHeight="24px" color="#949DB2">
              검색 결과가 없습니다
            </Text>
          </Message>
        );
    } else {
      if (pagination.pageState === false && !loading && !list.length)
        return (
          <Message>
            <Text lineHeight="24px" color="#949DB2">
              연동 신청 내역이 없습니다
            </Text>
          </Message>
        );
    }
  };

  const HeaderComponents = {
    rightComponent: () => {
      return (
        <Icon onClick={() => setFilterVisible(true)}> 
          <img src={SearchIcon} />
        </Icon>
      );
    },

    bottomComponent: () => {
      return (
        <BottomHeaderWrapper>
          {searchCount > 0 && (
            <FilterCard
              initialFilterValues={initialFilterValues}
              filter={filter}
              searchCount={searchCount}
              resetFilter={resetFilter}
              setFilter={setFilter}
            />
          )}
          <Tabs
            counts={getTabCounts()}
            options={TAB_OPTIONS}
            activeTab={activeTab}
            setActiveTab={handleClickTab}
          />
        </BottomHeaderWrapper>
      );
    },
  };

 

  const handleRefresh = () => {
    return new Promise((res) => {
      setTimeout(() => {
        dispatch(fetchRequests(true, { ...filter, activeTab })).then(() => {
          res();
        });
      }, 700);
    });
  };

  // runs only once when entered
  useEffect(() => {
    dispatch(fetchRequests(true, { ...filter, activeTab }));
  }, []);

  // runs only on filter || tab change
  useDidMountEffect(() => {
    dispatch(fetchRequests(false, { ...filter, activeTab }));
  }, [filter, activeTab]);

  // reset redux when going back
  useLayoutEffect(() => {
    return () => {
      dispatch(resetLinkRequests());
    };
  }, []);

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      resistance={2}
      pullingContent={<></>}
      refreshingContent={<PullToRefreshing />}
    >
      <MainLayout
        withBottomTab={false}
        withHeader={true}
        backgroundColor="white"
        headerProps={{
          padding: "0 20px 0 0", // right padding
          withBackButton: true,
          centerTitle: "회원앱 연동 관리",
          rightComponent: HeaderComponents.rightComponent,
          bottomComponent: HeaderComponents.bottomComponent,
        }}
      >
        {branchUseYn === "Y" && filter.seqPartnerBranch === 0 ? (
          <Message>
            <Text lineHeight="28px" fontSize="18px" color="#6B748B">
              지점별로 확인할 수 있어요.
            </Text>
            <Text lineHeight="24px" color="#949DB2">
              조회하고자 하는 지점을 선택해 주세요.
            </Text>
          </Message>
        ) : (
          <ListWrapper
            withFilterCard={searchCount > 0}
            withBranchSelect={showBranchSelect}
          >
            {getInfoMessage()}
            {list?.map((item) => {
              return (
                <AppLinkRequestCard
                  key={item.intlReqDtlId}
                  requestInfo={item}
                  onClick={() => setUserCard({ open: true, requestInfo: item })}
                />
              );
            })}
            <Observer
              isActive={pagination.pageState}
              isLoading={true}
              onVisible={() =>
                dispatch(fetchRequests(true, { ...filter, activeTab }))
              }
            />
          </ListWrapper>
        )}

        <AppLinkRequestPopup
          filter={{ ...filter, activeTab }}
          visible={userCard.open}
          onClose={() => setUserCard({ open: false, requestInfo: {} })}
          requestInfo={userCard.requestInfo}
          showBranchSelect={showBranchSelect}
        />

        <PopdownFilter
          visible={filterVisible}
          onClose={setFilterVisible}
          filter={filter}
          setFilter={setFilter}
          setSearchCount={setSearchCount}
        />

        <BottomBranchSelect
          searchBranch={filter.seqPartnerBranch}
          onChange={(branch) => handleFilterBranchChange(branch)}
        />
        {showBranchSelect && <BottomShadow />}
      </MainLayout>
    </PullToRefresh>
  );
};

const ListWrapper = styled.div`
  margin-top: ${(props) => (props.withFilterCard ? "108px" : "52px")};
  width: 100%;
  padding-bottom: ${(props) => (props.withBranchSelect ? "118px" : 0)};
`;

const BottomHeaderWrapper = styled.div``;

const BottomShadow = styled.div`
  width: 100%;
  height: 118px;
  position: fixed;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    #ffffff 78.12%,
    #ffffff 100%
  );
  z-index: 0;
  pointer-events: none;
`;

const Message = styled.div`
  width: 100%;
  position: absolute;
  top: 45%;
  text-align: center;
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  outline: none;
  position: relative;
  &:hover {
    cursor: pointer;
  }
`;

export default AppLinkRequests;
